import { useEffect, useRef } from 'react';

/**
 * Card tilt + moving glare (2026-10-10; touch screens 2026-10-14).
 *
 * Mouse: the element turns slightly toward the pointer and a soft highlight
 * follows it. Touch screens have no hover, so instead (while the card is on
 * screen): Android — the phone's own tilt drives it (deviceorientation needs
 * no permission there); iPhone — it follows the card's position as the page
 * scrolls (asking iOS for motion permission would mean a prompt); and a press
 * turns it toward the finger. Never with prefers-reduced-motion. The
 * transform is written straight onto the element, rAF-throttled. A child
 * marked [data-tilt-glare] receives the highlight.
 */
type Orient = { beta: number; gamma: number };
const orientSubs = new Set<(o: Orient) => void>();
let orientOn = false;
function onOrientation(e: DeviceOrientationEvent) {
  if (e.beta == null || e.gamma == null) return;
  const o = { beta: e.beta, gamma: e.gamma };
  orientSubs.forEach((f) => f(o));
}
function subscribeOrientation(f: (o: Orient) => void) {
  orientSubs.add(f);
  if (!orientOn) {
    orientOn = true;
    window.addEventListener('deviceorientation', onOrientation, { passive: true });
  }
  return () => {
    orientSubs.delete(f);
    if (!orientSubs.size && orientOn) {
      orientOn = false;
      window.removeEventListener('deviceorientation', onOrientation);
    }
  };
}
const needsMotionPermission = () =>
  typeof DeviceOrientationEvent !== 'undefined' &&
  typeof (DeviceOrientationEvent as unknown as { requestPermission?: unknown }).requestPermission === 'function';

export function useTilt<T extends HTMLElement>(maxDeg = 6) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.dataset.ownTilt = ''; // the global press-tilt (useCinPressTilt) skips this card
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const glare = el.querySelector<HTMLElement>('[data-tilt-glare]');
    let raf = 0;
    // Normalised 0…1 position of the "light" over the card.
    let px = 0.5;
    let py = 0.5;
    let lift = 3;

    const paint = () => {
      raf = 0;
      el.style.transform = `perspective(900px) rotateX(${((0.5 - py) * maxDeg).toFixed(2)}deg) rotateY(${((px - 0.5) * maxDeg).toFixed(2)}deg) translateY(-${lift}px)`;
      if (glare) {
        glare.style.opacity = '1';
        glare.style.background = `radial-gradient(380px circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%, rgba(255,255,255,0.28), transparent 60%)`;
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(paint);
    };
    const settle = (ms: number) => {
      el.style.transition = `transform ${ms}ms cubic-bezier(0.23,1,0.32,1), box-shadow 200ms ease`;
    };
    const reset = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      settle(450);
      el.style.transform = '';
      el.style.willChange = '';
      if (glare) glare.style.opacity = '0';
    };
    const fromPoint = (x: number, y: number) => {
      const r = el.getBoundingClientRect();
      px = Math.min(1, Math.max(0, (x - r.left) / r.width));
      py = Math.min(1, Math.max(0, (y - r.top) / r.height));
    };

    if (fine) {
      const onMove = (e: PointerEvent) => {
        fromPoint(e.clientX, e.clientY);
        lift = 3;
        schedule();
      };
      const onEnter = () => {
        settle(140);
        el.style.willChange = 'transform';
      };
      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', reset);
      return () => {
        cancelAnimationFrame(raf);
        el.removeEventListener('pointerenter', onEnter);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', reset);
      };
    }

    // ── Touch screens ──
    let visible = false;
    let pressed = false;
    let gotSensor = false;
    const ambient = () => {
      if (pressed || !visible) return;
      if (!gotSensor) {
        // Follow the scroll: top of the screen → light high, bottom → low.
        const r = el.getBoundingClientRect();
        const c = (r.top + r.height / 2) / window.innerHeight;
        px = 0.5 + 0.18 * Math.sin(c * Math.PI);
        py = Math.min(1, Math.max(0, c));
      }
      lift = 0;
      schedule();
    };
    const offOrient = needsMotionPermission()
      ? () => {}
      : subscribeOrientation(({ beta, gamma }) => {
          gotSensor = true;
          if (pressed || !visible) return;
          // Held upright the phone sits near beta 45°; ±25° spans the card.
          px = Math.min(1, Math.max(0, 0.5 + gamma / 50));
          py = Math.min(1, Math.max(0, 0.5 + (beta - 45) / 50));
          lift = 0;
          schedule();
        });
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible) {
        settle(260);
        ambient();
      } else reset();
    });
    io.observe(el);
    const onScroll = () => ambient();
    window.addEventListener('scroll', onScroll, { passive: true });
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      pressed = true;
      settle(140);
      fromPoint(e.clientX, e.clientY);
      lift = 3;
      schedule();
    };
    const onUp = () => {
      if (!pressed) return;
      pressed = false;
      settle(450);
      ambient();
    };
    el.addEventListener('pointerdown', onDown, { passive: true });
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      cancelAnimationFrame(raf);
      offOrient();
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [maxDeg]);

  return ref;
}

/** Responsive srcset for the Nano Banana images in /generated-images/v3 (480/800/1200 WebP). */
export const v3 = (name: string) => ({
  src: `/generated-images/v3/${name}-800.webp`,
  srcSet: [480, 800, 1200].map((w) => `/generated-images/v3/${name}-${w}.webp ${w}w`).join(', '),
});
